import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Button, Input, Select, SelectOption } from "@cinesys/ui/components";
import { MI_BASE } from "./config";
import { useCreateProfile } from "./service";

/**
 * Create a new persisted profile: name + workflow → create as Draft → navigate into the existing
 * profile tabs to configure. Deliberately not a wizard. Asset Import is selectable (stored as the
 * workflow) but stays non-executable for now — Collection Sync is the only configurable path today.
 */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 50;
`;
const Card = styled.div`
  background: var(--miSurface);
  border: 1px solid var(--miLine);
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 480px;
  padding: 24px 26px;
`;
const Title = styled.h2`
  margin: 0 0 18px;
  font-size: 18px;
  color: #fff;
`;
const Row = styled.div`
  margin-bottom: 16px;
`;
const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 22px;
`;
const Err = styled.p`
  color: var(--miDanger);
  font-size: 13px;
  margin: 8px 0 0;
`;

const WORKFLOW_OPTIONS: SelectOption[] = [
  { value: "collection-sync", label: "Collection Sync" },
  {
    value: "asset-import",
    label: "Asset Import (configuration only — executes in a later slice)",
  },
];

export default function NewProfileModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const create = useCreateProfile();
  const [name, setName] = useState("");
  const [workflow, setWorkflow] = useState("collection-sync");
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim()) {
      setError("Give the workflow a name.");
      return;
    }
    try {
      const created = await create.mutateAsync({ name: name.trim(), workflow });
      navigate(`${MI_BASE}/${created.id}`);
    } catch {
      setError("Could not create the workflow — is the local API running?");
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Title>New Workflow</Title>
        <Row>
          <Input
            label="Name"
            value={name}
            onChange={setName}
            placeholder="e.g. Program Metadata Sync"
          />
        </Row>
        <Row>
          <Select
            label="Workflow"
            options={WORKFLOW_OPTIONS}
            value={workflow}
            onChange={(o) =>
              setWorkflow(
                ((o as SelectOption)?.value as string) ?? "collection-sync"
              )
            }
          />
        </Row>
        {error && <Err>{error}</Err>}
        <Foot>
          <Button label="Cancel" type="default" onClick={onClose} />
          <Button
            label={create.isLoading ? "Creating…" : "Create Draft"}
            type="success"
            disabled={create.isLoading}
            onClick={submit}
          />
        </Foot>
      </Card>
    </Overlay>
  );
}
